import {announce} from '@github-ui/aria-live'
import {AttachmentErrorBanner} from '@github-ui/copilot-shared-ui/components/AttachmentErrorBanner'
import {EntitlementQuotaBanner} from '@github-ui/copilot-shared-ui/components/EntitlementQuotaBanner'
import {APPROVAL_MODE_PRESENTATION} from '@github-ui/copilot-shared-ui/utils/approval-mode-presentation'
import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {SUBMIT_ERROR_MESSAGE} from '@github-ui/ahp-session-client/submission-error'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {InlineMessage} from '@primer/react/experimental'
import {
  createAhpChatStartRequestWithProvisioningTarget,
  type AhpProvisioningTarget,
  type AhpSpaceSelection,
  type DirectTurnTelemetrySeed,
  type PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type Ref,
  type SetStateAction,
} from 'react'

import {CopilotComposerInput} from '../task/CopilotComposerInput'
import {isSandboxComposedDraft, isSessionTokenMeterApplicable} from '../task/use-environment-selection'

import {EntitlementTokenUsageMeter} from '../shared/TokenUsageMeterSurface'
import {useMarkdownImageHandling} from '../task/use-markdown-image-handling'
import {useTaskComposer, type TaskComposerTargetState} from '../task/use-task-composer'
import type {TaskComposerScopeState} from '../task/use-task-composer-scope'
import type {EnvironmentOption} from '../task/environment-strip/environment-option'
import {
  availableAgentModes,
  resolveAvailableAgentMode,
  scopeLogin,
  type ChatLaunch,
  type CreateTaskReference,
} from '../task/shared'
import type {HandleUserSubmit} from '../task/user-submit'
import type {Repository} from '@github-ui/current-repository'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {APPROVAL_MODES} from '@github-ui/ahp-relay/permission-mode'

import type {
  ComposerHandle,
  NewSessionConfiguration,
  NewSessionController,
  NewSessionEmptyStateContext,
  NewSessionProvisioningContext,
} from '../props'
import type {SharedSessionDraft} from './use-shared-session-draft'
import {resolveNewSessionApprovalMode} from './resolve-new-session-approval-mode'
import {useEnterpriseManagedSettings} from '../models/use-enterprise-managed-settings'
import {
  ENTERPRISE_MANAGED_SETTINGS_ERROR_MESSAGE,
  ENTERPRISE_MANAGED_SETTINGS_LOADING_MESSAGE,
  resolveEnterpriseManagedSettingsApprovalMode,
} from '../models/enterprise-managed-settings'
import {ToolbarButtonSkeleton} from '../task/task-chat-input-toolbar/ToolbarButtonSkeleton'
import styles from './NewSessionComposer.module.css'
import {useComposerTelemetry} from '../telemetry/ComposerTelemetryContext'
import {instrumentSubmit} from '../telemetry/composer-telemetry'

interface BranchState {
  selectedBranch: string
  setSelectedBranch: Dispatch<SetStateAction<string>>
}

interface EnvironmentState {
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: Dispatch<SetStateAction<EnvironmentOption | null>>
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
  isSandboxPolicyPending?: boolean
}

export interface NewSessionComposerControllerProps {
  controller: NewSessionController
  configuration: NewSessionConfiguration
  targetState: TaskComposerTargetState
  draft: SharedSessionDraft
  restorePrettifiedBlocks?: boolean
  branch: BranchState
  environment: EnvironmentState
  scope: TaskComposerScopeState
  initialTask?: {
    repository?: Repository
    repositoryNameWithOwner?: string
    reference?: CreateTaskReference
    branch?: string
  }
  initialPreparedPrompt?: PreparedPrompt
  initialChatProvisioningTarget?: AhpProvisioningTarget
  initialSpace?: AhpSpaceSelection
  initialBranch?: string
  initialTarget?: 'chat' | 'task'
  /** Keeps a Sandbox draft scoped to the launched repository's owner and withholds Chat. */
  repositoryLocked?: boolean
  suppressUrlRepository?: boolean
  initialSubmission?: {
    id: string
    prompt: string
    directTurnTelemetrySeed?: DirectTurnTelemetrySeed
  }
  scopeControl?: ReactNode
  draftSource?: string
  /** Conditions the parent owns, such as an unresolved Chat destination. */
  forceDisabled: boolean
  readOnly: boolean
  emptyState?: 'embedded'
  renderEmptyState?: (context: NewSessionEmptyStateContext, fallback: ReactNode) => ReactNode
  containerClassName?: string
  disableWhileSubmitting: boolean
  submitWithModifier: boolean
  clearOnSubmit: boolean
  maxTextareaHeight?: number
  handleRef?: Ref<ComposerHandle>
  disableAutoFocus?: boolean
  onSubmittingChange: (isSubmitting: boolean) => void
  onBeforeSubmit?: () => void
  belowComposerContent?: ReactNode
}

/**
 * The shared composing surface for a session that has not started yet.
 *
 * One controller owns the draft, models, attachments, and submission behavior
 * while environment and target changes update the controls around the input.
 */
const rejectMissingTaskDestination: HandleUserSubmit = () => {
  throw new Error('This surface cannot create tasks, so the repository destination should not have been offered.')
}

export function NewSessionComposerController({
  controller: host,
  configuration,
  targetState,
  draft,
  restorePrettifiedBlocks,
  branch,
  environment,
  scope,
  initialTask,
  initialPreparedPrompt,
  initialChatProvisioningTarget,
  initialSpace,
  initialBranch,
  initialTarget,
  repositoryLocked = false,
  suppressUrlRepository,
  initialSubmission,
  scopeControl,
  draftSource,
  forceDisabled,
  readOnly,
  emptyState,
  renderEmptyState,
  containerClassName,
  disableWhileSubmitting,
  submitWithModifier,
  clearOnSubmit,
  maxTextareaHeight,
  handleRef,
  disableAutoFocus,
  onSubmittingChange,
  onBeforeSubmit,
  belowComposerContent,
}: NewSessionComposerControllerProps) {
  const {textAreaRef} = targetState
  const images = useMarkdownImageHandling({
    textAreaRef,
    repositoryId: targetState.selectedRepo?.id?.toString(),
  })
  const focusComposer = useCallback(() => textAreaRef.current?.focus(), [textAreaRef])
  const chatLaunch = useMemo<ChatLaunch | undefined>(() => {
    if (host.chatLaunch?.kind === 'environment') {
      if (!host.createEnvironmentTask) return undefined
      return {
        kind: 'environment',
        ...(host.chatLaunch.availability ? {availability: host.chatLaunch.availability} : {}),
      }
    }

    const hostChatLaunch = host.chatLaunch
    const onSubmit = hostChatLaunch?.onSubmit
    if (!onSubmit) return undefined

    return {
      kind: 'handoff',
      ...(hostChatLaunch.submitRepositoryAsChat ? {submitRepositoryAsChat: true} : {}),
      onSubmit: async (prompt, metadata) => {
        const target: AhpProvisioningTarget | undefined = metadata?.repositoryNameWithOwner
          ? {
              kind: 'repository',
              nameWithOwner: metadata.repositoryNameWithOwner,
              ...(metadata.scopeLogin ? {scopeOwner: metadata.scopeLogin} : {}),
            }
          : metadata?.scopeLogin
            ? {kind: 'scope', owner: metadata.scopeLogin}
            : undefined
        const request = createAhpChatStartRequestWithProvisioningTarget({
          prompt,
          target,
          ...(metadata?.space ? {space: metadata.space, initialSelectedSpace: metadata.space} : {}),
        })
        // Handoffs may carry a genuinely explicit repository target in metadata,
        // but attached repository references remain prompt context only.
        await onSubmit(
          {
            ...request,
            ...(metadata?.repositoryNameWithOwner
              ? {metadata: {...request.metadata, repository: metadata.repositoryNameWithOwner}}
              : {}),
          },
          metadata?.directTurnTelemetrySeed,
        )
      },
    }
  }, [host.chatLaunch, host.createEnvironmentTask])
  const initialPrompt =
    initialTarget && initialPreparedPrompt ? {...initialPreparedPrompt, attachments: undefined} : initialPreparedPrompt
  const isSandboxModeDraft = isSandboxComposedDraft({
    isSandboxEnvironment: environment.isSandboxEnvironment,
    selectedEnvironmentKind: environment.selectedEnvironment?.kind,
    deliversByHandoff:
      chatLaunch?.kind === 'handoff' &&
      Boolean(configuration.supportsManagedSandbox || environment.isSandboxPolicyPending),
  })
  const isSessionModeDraft = isSandboxModeDraft || environment.isAhpEnvironment
  const availableModes = availableAgentModes(isSessionModeDraft)
  const effectiveMode = resolveAvailableAgentMode(configuration.mode, isSessionModeDraft) ?? availableModes[0]
  const rememberToolPermissionsEnabled = isFeatureEnabled('one_copilot_remember_tool_permissions')
  const enterpriseManagedSettings = useEnterpriseManagedSettings(configuration)
  const approvalMode =
    enterpriseManagedSettings &&
    !isSandboxModeDraft &&
    !isFeatureEnabled('copilot_mission_control_independent_mode_controls')
      ? undefined
      : configuration.approvalMode
  const enterpriseSettingsApply = isSandboxModeDraft && enterpriseManagedSettings !== undefined
  const settingsPending = enterpriseSettingsApply && enterpriseManagedSettings.status === 'pending'
  const settingsLoadFailed = enterpriseSettingsApply && enterpriseManagedSettings.status === 'error'
  useEffect(() => {
    if (settingsPending) announce(ENTERPRISE_MANAGED_SETTINGS_LOADING_MESSAGE)
    else if (settingsLoadFailed) announce(ENTERPRISE_MANAGED_SETTINGS_ERROR_MESSAGE)
  }, [settingsPending, settingsLoadFailed])
  const settingsApprovalMode = enterpriseSettingsApply
    ? resolveEnterpriseManagedSettingsApprovalMode(approvalMode ?? 'assisted', enterpriseManagedSettings)
    : approvalMode
  const availableApprovalModes = enterpriseSettingsApply
    ? enterpriseManagedSettings.permissions.availableApprovalModes.filter(
        mode =>
          configuration.availableApprovalModes === undefined || configuration.availableApprovalModes.includes(mode),
      )
    : configuration.availableApprovalModes
  const resolvedApprovalMode =
    settingsApprovalMode && (enterpriseSettingsApply || rememberToolPermissionsEnabled)
      ? resolveNewSessionApprovalMode(settingsApprovalMode, availableApprovalModes)
      : settingsApprovalMode
  const isApprovalModeBlocked =
    settingsPending ||
    ((enterpriseSettingsApply || rememberToolPermissionsEnabled) &&
      isSessionModeDraft &&
      (enterpriseSettingsApply || approvalMode !== undefined) &&
      resolvedApprovalMode === undefined)
  const blockedApprovalMessage =
    isApprovalModeBlocked && !settingsPending && !readOnly
      ? availableApprovalModes?.length
        ? `Tool permissions: "${APPROVAL_MODE_PRESENTATION[configuration.approvalMode ?? settingsApprovalMode ?? 'manual'].label}" isn't available for this session. Choose an available option to continue.`
        : "Tool permissions: No options are available for this session, so it can't be started."
      : undefined
  useEffect(() => {
    if (blockedApprovalMessage) announce(blockedApprovalMessage)
  }, [blockedApprovalMessage])
  const controller = useTaskComposer(
    {
      handleUserSubmit: host.createTask ?? rejectMissingTaskDestination,
      onEnvironmentSubmit: host.createEnvironmentTask,
      agentMode: configuration.mode ? effectiveMode : undefined,
      approvalMode: isSessionModeDraft ? resolvedApprovalMode : undefined,
      approvalModeConstraints:
        enterpriseSettingsApply && enterpriseManagedSettings.status === 'ready' ? availableApprovalModes : undefined,
      submissionBlockedReason: isApprovalModeBlocked
        ? settingsPending
          ? ENTERPRISE_MANAGED_SETTINGS_LOADING_MESSAGE
          : 'No permitted tool permissions are available for this session.'
        : undefined,
      initialRepo: initialTask?.repository,
      initialRepositoryNameWithOwner: initialTask?.repositoryNameWithOwner,
      initialReference: initialTask?.reference,
      initialText: draft.currentPrompt,
      initialPreparedPrompt: initialPrompt,
      initialTaskAttachments: initialTarget === 'task' ? initialPreparedPrompt?.attachments : undefined,
      initialChatAttachments: initialTarget === 'chat' ? initialPreparedPrompt?.attachments : undefined,
      initialChatProvisioningTarget: initialTarget === 'chat' ? initialChatProvisioningTarget : undefined,
      initialSpace: initialTarget === 'chat' ? initialSpace : undefined,
      chatLaunch,
      eventType: draftSource,
      supportsManagedSandbox: configuration.supportsManagedSandbox,
      agentQuotaSnapshot: configuration.agentQuota,
      mcpPermissionGateEnabled: configuration.mode === undefined ? configuration.mcpPermissionGateEnabled : false,
      currentUserLogin: configuration.currentUserLogin,
      currentUserAvatarUrl: configuration.currentUserAvatarUrl,
      availableOrganizations: configuration.availableOrganizations,
      hideRepoSelector: repositoryLocked,
      initialBranch,
      initialTarget,
      suppressUrlRepository,
      includeImplicitContextForTask: draftSource === 'quick_chat_panel',
      directTurnTelemetrySurface: configuration.directTurnTelemetrySurface,
      preserveDirectTurnTelemetryForHandoff: configuration.preserveDirectTurnTelemetryForHandoff,
    },
    {targetState, images, draft, branch, environment, scope},
  )
  useImperativeHandle(
    handleRef,
    () => ({
      addFiles: files => {
        if (controller.isAhpEnvironment) {
          void controller.environmentAttachments.handleFilesAdded(files, 'drop')
        } else {
          images.addFiles(files)
        }
      },
    }),
    [controller.environmentAttachments, controller.isAhpEnvironment, images],
  )
  const sessionModeRef = useRef<AgentMode>(configuration.mode ?? 'interactive')
  const actionsModeRef = useRef<AgentMode>(configuration.mode === 'autopilot' ? 'autopilot' : 'interactive')
  const previousSessionModeDraftRef = useRef(isSessionModeDraft)
  useEffect(() => {
    const mode = configuration.mode
    const onModeChange = configuration.onModeChange
    const wasSessionModeDraft = previousSessionModeDraftRef.current

    if (wasSessionModeDraft !== isSessionModeDraft) {
      if (mode) {
        if (wasSessionModeDraft) {
          sessionModeRef.current = mode
        } else if (mode !== 'plan') {
          actionsModeRef.current = mode
        }
      }

      previousSessionModeDraftRef.current = isSessionModeDraft
      const destinationMode = isSessionModeDraft ? sessionModeRef.current : actionsModeRef.current
      if (onModeChange && mode !== destinationMode) onModeChange(destinationMode)
      return
    }

    if (!mode) return
    if (isSessionModeDraft) {
      sessionModeRef.current = mode
    } else if (mode === 'plan') {
      onModeChange?.(actionsModeRef.current)
    } else {
      actionsModeRef.current = mode
    }
  }, [configuration.mode, configuration.onModeChange, isSessionModeDraft])
  const displayedApprovalMode = resolvedApprovalMode ?? settingsApprovalMode ?? approvalMode
  const approval =
    isSessionModeDraft && displayedApprovalMode && configuration.onApprovalModeChange
      ? {
          selectedMode: displayedApprovalMode,
          availableModes: availableApprovalModes ?? APPROVAL_MODES,
          unavailableReason:
            enterpriseSettingsApply && enterpriseManagedSettings.status !== 'none'
              ? enterpriseManagedSettings.status === 'error'
                ? ENTERPRISE_MANAGED_SETTINGS_ERROR_MESSAGE
                : 'Disabled by your organization. Contact your administrator'
              : undefined,
          onModeChange: (mode: (typeof APPROVAL_MODES)[number]) => {
            if (availableApprovalModes && !availableApprovalModes.includes(mode)) return
            if (mode !== displayedApprovalMode) {
              telemetry?.track('approval_mode_changed', {
                approval_kind: 'session',
                from_mode: displayedApprovalMode,
                to_mode: mode,
              })
            }
            configuration.onApprovalModeChange?.(mode)
          },
        }
      : undefined
  const modePicker = settingsPending ? (
    <div role="group" aria-label={ENTERPRISE_MANAGED_SETTINGS_LOADING_MESSAGE}>
      <ToolbarButtonSkeleton width="100px" />
    </div>
  ) : (
    configuration.modePicker?.({availableModes, approval})
  )
  const leadingControl = scopeControl ? (
    <>
      {scopeControl}
      {modePicker}
    </>
  ) : (
    modePicker
  )

  useEffect(() => {
    onSubmittingChange(controller.isEnvironmentSubmitting)
    return () => onSubmittingChange(false)
  }, [onSubmittingChange, controller.isEnvironmentSubmitting])

  const readinessUserActionPending =
    !controller.isRepositorySelectionValid ||
    controller.isScopeBlockedBySso ||
    (controller.isAhpEnvironment && !environment.selectedEnvironment?.environmentId)
  const readinessStatePending =
    settingsPending ||
    readinessUserActionPending ||
    controller.isSpaceResolving ||
    (environment.selectedEnvironment?.kind === 'sandbox' && environment.isSandboxPolicyPending === true) ||
    (controller.isAhpEnvironment
      ? configuration.disableSandboxSubmission ||
        (controller.targetSelection.kind === 'chat' ? !chatLaunch : !host.createEnvironmentTask)
      : !controller.isModelSelectionReady)
  const disabled =
    forceDisabled ||
    isApprovalModeBlocked ||
    controller.isBlockedByQuota ||
    controller.isEnvironmentSubmitting ||
    controller.isImageUploading ||
    readinessStatePending

  const telemetry = useComposerTelemetry()
  const earlyProvisioning = host.earlyProvisioning
  const provisioningContext = useMemo<NewSessionProvisioningContext>(
    () => ({
      target: controller.targetSelection.kind === 'chat' ? 'chat' : 'task',
      scope: scope.launchScopeLogin ?? scopeLogin(scope.selectedScope, controller.resolvedUserLogin),
      space: controller.selectedSpace
        ? {owner: controller.selectedSpace.owner, slug: controller.selectedSpace.slug}
        : undefined,
      repository: controller.provisioningRepository,
      branch: branch.selectedBranch,
      environmentId: environment.selectedEnvironment?.environmentId,
    }),
    [
      controller.targetSelection.kind,
      controller.resolvedUserLogin,
      controller.selectedSpace,
      controller.provisioningRepository,
      scope.launchScopeLogin,
      scope.selectedScope,
      branch.selectedBranch,
      environment.selectedEnvironment?.environmentId,
    ],
  )
  useLayoutEffect(() => {
    if (settingsPending) return
    earlyProvisioning?.onContextChange(provisioningContext)
  }, [earlyProvisioning, provisioningContext, settingsPending])
  useEffect(() => () => earlyProvisioning?.onDispose(), [earlyProvisioning])
  const onInteraction = useCallback(() => {
    if (readOnly || disabled) return
    if (provisioningContext.target === 'task' && provisioningContext.repository) {
      earlyProvisioning?.onRepositoryInteraction?.(provisioningContext)
    } else {
      earlyProvisioning?.onInteraction(provisioningContext)
    }
  }, [disabled, earlyProvisioning, provisioningContext, readOnly])
  const beforeSubmit = useCallback(() => {
    if (isApprovalModeBlocked) throw new ChatInputValidationError('Tool permissions are not ready.')
    earlyProvisioning?.onBeforeSubmit(provisioningContext)
    onBeforeSubmit?.()
  }, [earlyProvisioning, onBeforeSubmit, provisioningContext, isApprovalModeBlocked])

  const submittedInitialIdRef = useRef<string | undefined>(undefined)
  const [initialSubmitError, setInitialSubmitError] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (
      !initialSubmission ||
      submittedInitialIdRef.current === initialSubmission.id ||
      initialSubmitError ||
      disabled ||
      draft.currentPrompt !== initialSubmission.prompt
    ) {
      return
    }

    submittedInitialIdRef.current = initialSubmission.id
    void (async () => {
      try {
        beforeSubmit()
        const prompt = draft.currentPrompt
        await instrumentSubmit(telemetry, 'auto_submit', prompt.length, () =>
          controller.onSubmit(prompt, initialSubmission.directTurnTelemetrySeed, true),
        )
      } catch (error) {
        submittedInitialIdRef.current = undefined
        const message = error instanceof ChatInputValidationError ? error.message : SUBMIT_ERROR_MESSAGE
        setInitialSubmitError(message)
        // The banner is the only visual report, so the task path announces it.
        // Sandbox Chat sends announce their own failure and never come here.
        announce(message)
      }
    })()
  }, [controller, disabled, draft.currentPrompt, initialSubmitError, initialSubmission, beforeSubmit, telemetry])

  return (
    <>
      {!readOnly && (
        <EntitlementQuotaBanner
          notice={controller.quotaNotice}
          focusAfterDismiss={focusComposer}
          className={styles.QuotaNotice}
        />
      )}
      <CopilotComposerInput
        controller={controller}
        restorePrettifiedBlocks={restorePrettifiedBlocks}
        value={draft.currentPrompt}
        onValueChange={controller.handleTextChange}
        textAreaRef={textAreaRef}
        disabled={disabled}
        readinessStatePending={readinessStatePending}
        readinessUserActionPending={readinessUserActionPending}
        readOnly={readOnly}
        emptyState={emptyState}
        renderEmptyState={renderEmptyState}
        containerClassName={containerClassName}
        submitWithModifier={submitWithModifier}
        clearOnSubmit={clearOnSubmit}
        disableWhileSubmitting={disableWhileSubmitting}
        maxTextareaHeight={maxTextareaHeight}
        disableAutoFocus={disableAutoFocus}
        responsiveIconOnly
        leadingControl={leadingControl}
        // A Sandbox task spends the same premium quota a Sandbox Chat does, so
        // the meter follows the draft rather than the destination.
        meter={
          isSessionTokenMeterApplicable({
            isTokenBasedBillingEnabled: controller.isTokenBasedBillingEnabled,
            isSandboxEnvironment: controller.isSandboxDraft,
          }) ? (
            <EntitlementTokenUsageMeter />
          ) : undefined
        }
        belowComposerContent={belowComposerContent}
        onBeforeSubmit={beforeSubmit}
        onInteraction={earlyProvisioning ? onInteraction : undefined}
      />
      {blockedApprovalMessage && <InlineMessage variant="warning">{blockedApprovalMessage}</InlineMessage>}
      {initialSubmitError && (
        <AttachmentErrorBanner
          messages={[initialSubmitError]}
          errorCode="message_submit_failed"
          onDismiss={() => setInitialSubmitError(undefined)}
          title="Submission error"
        />
      )}
    </>
  )
}

NewSessionComposerController.displayName = 'NewSessionComposerController'
